import {
  CapabilityPage,
  type CapabilitySection,
} from "@/components/marketing/CapabilityPage";
import { ProductionMapExplainer } from "@/components/marketing/ProductionMapExplainer";

/**
 * ``/features/vyroba-a-dilna`` — what happens between a paid order and a
 * finished part.
 *
 * The four sections follow the order a shop actually meets them in:
 * where the work stands, the terminal the operator uses, the inspection,
 * and the partner it goes to when the shop cannot take it all.
 *
 * **Only four items carry a plan key** — the two paid shop-floor layers
 * and the two multi-vendor screens. Boards, the production map, machine
 * capacity, workstations, labels, traceability and the whole of quality
 * are on every tier, so they name no entitlement and wear no badge.
 * Quality is worth a line of its own: the ``quality`` entitlement was
 * deleted outright by ``gt06_gates_to_base_and_node_budget`` after
 * customer feedback, and the screens are unlocked today by a
 * ``kind:gate`` node in the production map plus the ``quality`` app
 * install (``qc01_install_quality_app``). A badge here would invent a
 * tier that no longer exists.
 */
const SECTIONS: readonly CapabilitySection[] = [
  // Where the work stands. One board with seven axes, the map the board
  // reads from, and the two numbers that say whether the date holds.
  {
    id: "overview",
    items: [
      // The lede of the page, not just of this section: „what do I start
      // on Monday" is the question a shop owner actually opens with.
      { id: "critical", wide: true },
      { id: "map" },
      { id: "capacity" },
      { id: "promise" },
    ],
    shot: "production-overview",
  },
  // The floor itself. The two paid flow layers sit here because that is
  // where an operator meets them, not in a pricing footnote.
  {
    id: "shopfloor",
    items: [
      { id: "places" },
      { id: "labels" },
      { id: "flow2", entitlement: "shopfloor.flow2" },
      { id: "flow3", entitlement: "shopfloor.flow3" },
    ],
    shot: "production-shopfloor",
  },
  { id: "quality", items: [{ id: "gates" }, { id: "ncr" }, { id: "protocol" }, { id: "trace" }] },
  // The one section that wants reading rather than skimming: handing a
  // customer to somebody else is the decision on this page a shop owner
  // is slowest to make. The ink block is where the landing puts exactly
  // that kind of claim.
  {
    id: "partners",
    tone: "ink",
    items: [
      { id: "handoff", entitlement: "multi_vendor" },
      { id: "ranking", entitlement: "multi_vendor" },
    ],
  },
];

const PAINS = ["ask", "sheet", "late"] as const;
const GAINS = ["calls", "onesource", "early"] as const;

export default function FeaturesProduction(): JSX.Element {
  return (
    <CapabilityPage
      i18nKey="features.pages.production"
      path="/features/vyroba-a-dilna"
      pains={PAINS}
      gains={GAINS}
      sections={SECTIONS}
      explainer={<ProductionMapExplainer />}
    />
  );
}
